import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export interface RevokeMandateInput {
  reason: string;
  effectiveDate?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPending?: boolean;
  onSubmit: (input: RevokeMandateInput) => void;
}

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Captures the mandatory reason (and optional effective date) before revoking a
 * mandate. Revoking cancels the mandate's unsent collections and is irreversible.
 */
const RevokeMandateDialog: React.FC<Props> = ({ open, onOpenChange, isPending, onSubmit }) => {
  const [reason, setReason] = useState("");
  const [effectiveDate, setEffectiveDate] = useState<string>(today());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setEffectiveDate(today());
      setError(null);
    }
  }, [open]);

  const handleSubmit = () => {
    if (!reason.trim()) {
      setError("Bitte einen Grund angeben");
      return;
    }
    onSubmit({ reason: reason.trim(), effectiveDate });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl">Mandat widerrufen</DialogTitle>
          <DialogDescription className="text-[15px]">
            Nicht eingereichte Lastschriften dieses Mandats werden storniert. Der Widerruf kann
            nicht rückgängig gemacht werden – für eine neue Einwilligung ein neues Mandat anlegen.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-1.5">
            <Label className="text-base">Grund</Label>
            <Textarea
              value={reason}
              disabled={isPending}
              rows={3}
              placeholder="z. B. vom Patienten widerrufen"
              onChange={(e) => setReason(e.target.value)}
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <div className="space-y-1.5">
            <Label className="text-base">Gültig ab</Label>
            <Input
              type="date"
              value={effectiveDate}
              disabled={isPending}
              onChange={(e) => setEffectiveDate(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isPending}>
            Abbrechen
          </Button>
          <Button variant="destructive" onClick={handleSubmit} disabled={isPending}>
            {isPending ? "Wird widerrufen…" : "Widerrufen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RevokeMandateDialog;
